import React from 'react'
import { Check } from 'lucide-react'

interface PricingCardProps {
  title: string
  price: number
  period: 'monthly' | 'annually'
  priceId: string
  features: string[]
  popular?: boolean
  onSelectPlan: (priceId: string) => void
  isLoading?: boolean
}

export const PricingCard: React.FC<PricingCardProps> = ({
  title,
  price,
  period,
  priceId,
  features,
  popular = false,
  onSelectPlan,
  isLoading = false,
}) => {
  const annualSavings = period === 'annually' ? Math.round((125 * 12 - price) / (125 * 12) * 100) : 0

  return (
    <div
      className={`relative bg-dark-secondary/50 backdrop-blur-sm border rounded-3xl p-8 transition-all ${
        popular
          ? 'border-primary-500 shadow-2xl scale-105'
          : 'border-primary-600/20 hover:border-primary-600/40'
      }`}
    >
      {popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-light-primary mb-2">{title}</h3>

        <div className="flex items-baseline justify-center mb-2">
          <span className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400">
            ${price}
          </span>
          <span className="text-light-tertiary ml-2">
            /{period === 'monthly' ? 'month' : 'year'}
          </span>
        </div>

        {period === 'annually' && annualSavings > 0 && (
          <div className="inline-block">
            <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-medium">
              Save {annualSavings}%
            </span>
          </div>
        )}

        <p className="text-light-secondary text-sm mt-3">
          7-day free trial
        </p>
      </div>

      <div className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <div key={index} className="flex items-start">
            <div className="w-5 h-5 bg-primary-600/20 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
              <Check className="w-3 h-3 text-primary-400" />
            </div>
            <span className="text-light-secondary text-sm leading-relaxed">{feature}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => onSelectPlan(priceId)}
        disabled={isLoading}
        className={`w-full py-4 rounded-xl font-semibold text-lg transition-all transform ${
          popular
            ? 'bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white shadow-xl hover:scale-105'
            : 'bg-dark-tertiary hover:bg-dark-quaternary text-light-primary border border-primary-600/30'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isLoading ? 'Processing...' : 'Start Free Trial'}
      </button>
    </div>
  )
}
